import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  useAddReportToDashboard,
  useAddToDashboard,
  useAddToReport,
  useCreateDashboard,
  useDashboards,
  useReports,
  useSaveReport,
} from "@/api/queries";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/Button";
import { Menu } from "@/components/ui/Menu";
import { Modal } from "@/components/ui/Modal";
import { CheckIcon, DashboardIcon, PinIcon, PlusIcon, ReportIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * The two ways an answer leaves the conversation (PRD FR-7.1, FR-7.3).
 *
 * **Save as report** pins the whole answer — its narrative and one tile per
 * artifact. **Add to…** pins one artifact onto a dashboard. Both copy: the
 * conversation can be deleted afterwards and nothing here changes.
 */

export function SaveAsReport({ messageId, disabled }: { messageId: string; disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const save = useSaveReport();
  const navigate = useNavigate();

  async function submit() {
    setFailure(null);
    try {
      const report = await save.mutateAsync({ message_id: messageId, title });
      setOpen(false);
      setTitle("");
      await navigate({ to: "/reports/$reportId", params: { reportId: report.id } });
    } catch (error) {
      setFailure(
        error instanceof ApiError ? error.message : "This answer could not be saved as a report.",
      );
    }
  }

  return (
    <>
      <Button
        size="sm"
        icon={<PinIcon className="size-3.5" />}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        Save as report
      </Button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Save as report"
        description="Captures the question, the summary, the insights and the SQL, and lays out one tile per artifact. It keeps working if this conversation is deleted."
        footer={
          <>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => void submit()}>
              Save report
            </Button>
          </>
        }
      >
        <label className="flex flex-col gap-1.5">
          <span className="type-label text-ink">Title</span>
          <input
            value={title}
            autoFocus
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Defaults to the answer's title"
            className="bg-surface border-border rounded-element control-lg type-body text-ink focus:border-border-emphasized motion-fast w-full border px-3 outline-none"
          />
        </label>
        {failure ? (
          <p className="bg-error-subtle text-error type-support rounded-element mt-3 px-3 py-2">
            {failure}
          </p>
        ) : null}
      </Modal>
    </>
  );
}

/**
 * "Add to…" for one chat artifact, a whole answer, or a whole report.
 *
 * One menu rather than several buttons: the choice a person is making is
 * *which surface*, and the thing being added is already on screen.
 *
 * **An artifact can go to a report as well as a dashboard.** A report used to
 * be reachable only through "Save as report", which meant a report could only
 * ever be everything one answer produced. Both destinations take the same
 * copy, so a report someone is assembling keeps growing from the conversation
 * that is feeding it. A *report's own tiles* still only go to dashboards —
 * folding one report into another is a shape nothing has asked for, and the
 * menu is shorter for not offering it.
 */
export function AddTo({
  messageId,
  artifactIndex,
  reportId,
  label,
}: {
  messageId?: string;
  /** One artifact, or the whole answer when omitted. */
  artifactIndex?: number;
  /** Add every tile of a report instead (PRD FR-7.3). */
  reportId?: string;
  label?: string;
}) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [added, setAdded] = useState<string | null>(null);
  const { data: dashboards } = useDashboards();
  // Only the artifact case can target a report, so only it pays for the list.
  const { data: reports } = useReports();
  const add = useAddToDashboard();
  const addToReport = useAddToReport();
  const addReport = useAddReportToDashboard();
  const create = useCreateDashboard();

  // `close` is threaded through rather than held in state: the menu owns
  // whether it is open, and this component only ever needs to dismiss it.
  // Failures here are announced by the mutation cache (backlog item 11), so
  // these only have to stop short of claiming the tile was added.
  async function addTo(dashboardId: string, close: () => void) {
    try {
      if (reportId) {
        await addReport.mutateAsync({ dashboardId, reportId });
      } else if (messageId) {
        await add.mutateAsync({ dashboardId, messageId, artifactIndex: artifactIndex ?? null });
      }
    } catch {
      return;
    }
    confirmAdded(dashboardId, close);
  }

  async function addToExistingReport(target: string, close: () => void) {
    if (!messageId) return;
    try {
      await addToReport.mutateAsync({
        reportId: target,
        messageId,
        artifactIndex: artifactIndex ?? null,
      });
    } catch {
      return;
    }
    confirmAdded(target, close);
  }

  function confirmAdded(id: string, close: () => void) {
    setAdded(id);
    close();
    window.setTimeout(() => setAdded(null), 2000);
  }

  async function createAndAdd(close: () => void) {
    if (!name.trim()) return;
    const dashboard = await create.mutateAsync({ name }).catch(() => null);
    if (!dashboard) return;
    setName("");
    setCreating(false);
    await addTo(dashboard.id, close);
  }

  const item =
    "type-support text-ink hover:bg-hover rounded-inner motion-fast flex w-full items-center gap-2 px-2 py-1.5 text-start";

  return (
    <Menu
      label={reportId ? "Add to dashboard" : "Add to a report or dashboard"}
      className="w-64"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          className={cn(
            "type-caption text-ink-secondary hover:text-ink hover:bg-hover motion-fast",
            "rounded-inner inline-flex items-center gap-1 px-2 py-1",
          )}
        >
          {added ? (
            <CheckIcon className="text-success size-3.5" />
          ) : (
            <PlusIcon className="size-3.5" />
          )}
          {added ? "Added" : (label ?? "Add to…")}
        </button>
      )}
    >
      {(close) => (
        <>
          <p className="type-caption text-ink-disabled px-2 py-1">Add to dashboard</p>
          {(dashboards ?? []).map((dashboard) => (
            <button
              key={dashboard.id}
              type="button"
              role="menuitem"
              className={item}
              onClick={() => void addTo(dashboard.id, close)}
            >
              <DashboardIcon className="size-3.5" />
              <span className="min-w-0 flex-1 truncate">{dashboard.name}</span>
              <span className="type-caption text-ink-disabled">{dashboard.tile_count}</span>
            </button>
          ))}
          {!dashboards?.length && !creating ? (
            <p className="type-support text-ink-secondary px-2 py-1.5">No dashboards yet.</p>
          ) : null}

          {!reportId && reports?.length ? (
            <>
              <div className="border-border my-1 border-t" />
              <p className="type-caption text-ink-disabled px-2 py-1">Add to report</p>
              {reports.map((report) => (
                <button
                  key={report.id}
                  type="button"
                  role="menuitem"
                  className={item}
                  onClick={() => void addToExistingReport(report.id, close)}
                >
                  <ReportIcon className="size-3.5" />
                  <span className="min-w-0 flex-1 truncate">{report.title}</span>
                  <span className="type-caption text-ink-disabled">{report.tile_count}</span>
                </button>
              ))}
            </>
          ) : null}

          <div className="border-border my-1 border-t" />
          {creating ? (
            <div className="flex flex-col gap-2 p-1">
              <input
                value={name}
                autoFocus
                onChange={(event) => setName(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && void createAndAdd(close)}
                placeholder="Dashboard name"
                className="bg-surface border-border rounded-inner control-md type-support text-ink w-full border px-2 outline-none"
              />
              <Button size="sm" variant="primary" onClick={() => void createAndAdd(close)}>
                Create and add
              </Button>
            </div>
          ) : (
            <button type="button" role="menuitem" className={item} onClick={() => setCreating(true)}>
              <PlusIcon className="size-3.5" />
              New dashboard…
            </button>
          )}
        </>
      )}
    </Menu>
  );
}
